import React, { useEffect, useState } from "react";
import { DatePicker } from "@/components/DatePicker";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/dateFormat";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Building2 } from "lucide-react";
import { OverbookingDialog } from "@/components/OverbookingDialog";

export default function LargeConsumerModule() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [overCheck, setOverCheck] = useState(null);
  const [dayAvail, setDayAvail] = useState(null);
  const [f, setF] = useState({
    date: new Date().toISOString().slice(0, 10),
    volume_m3: "",
    category: "agriculture",
    description: "",
  });

  const load = () => api.get("/large-consumer-requests").then((r) => setItems(r.data));

  const loadAvail = (day) =>
    api.get(`/quota/availability?day=${day}`).then((r) => setDayAvail(r.data)).catch(() => setDayAvail(null));

  useEffect(() => {
    if (user.role === "pool_owner" && f.date) {
      loadAvail(f.date);
    }
  }, [f.date, user.role]);
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/large-consumer-requests", {
      date: f.date,
      volume_m3: parseFloat(f.volume_m3),
      category: f.category,
      description: f.description,
    });
    toast.success(t("request_water") + " ✓");
    setF({ date: new Date().toISOString().slice(0, 10), volume_m3: "", category: "agriculture", description: "" });
    load();
    loadAvail(f.date);
  };

  const setStatus = async (id, status) => {
    await api.patch(`/large-consumer-requests/${id}`, { status });
    load();
  };

  // Pending requests are already counted in the day's usage, so overbooked == used > quota.
  const approve = async (r) => {
    const { data } = await api.get(`/quota/availability?day=${r.date}`);
    if (data.used_m3 > data.quota_m3) {
      setOverCheck({ id: r.id, date: r.date, volume: r.volume_m3, used: data.used_m3, quota: data.quota_m3 });
      return;
    }
    await setStatus(r.id, "approved");
  };

  const confirmOverbooking = async () => {
    const id = overCheck.id;
    setOverCheck(null);
    await setStatus(id, "approved");
    toast.warning(t("overbooking_approved_toast"));
  };

  return (
    <div className="space-y-4">
      {user.role === "pool_owner" && (
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">{t("request_water")}</h3>
            </div>
            <form onSubmit={submit} className="grid gap-4 sm:grid-cols-4">
              <div>
                <Label>{t("date")}</Label>
                <DatePicker value={f.date} onChange={(v) => setF({ ...f, date: v })} testId="lc-date" />
              </div>
              <div>
                <Label>{t("volume_m3")}</Label>
                <Input type="number" step="0.1" value={f.volume_m3} onChange={(e) => setF({ ...f, volume_m3: e.target.value })} required data-testid="lc-volume" />
              </div>
              <div>
                <Label>{t("category")}</Label>
                <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                  <SelectTrigger data-testid="lc-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agriculture">{t("agriculture")}</SelectItem>
                    <SelectItem value="industry">{t("industry")}</SelectItem>
                    <SelectItem value="event">{t("event")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-4">
                <DayAvailabilityHint avail={dayAvail} volume={parseFloat(f.volume_m3) || 0} t={t} />
              </div>
              <div className="flex items-end">
                <Button type="submit" className="w-full" data-testid="lc-submit">{t("submit")}</Button>
              </div>
              <div className="sm:col-span-4">
                <Label>{t("description")}</Label>
                <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="lc-desc" />
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="mb-4 text-lg font-semibold">{t("large_consumer")}</h3>
          {items.length === 0 ? (
            <div className="text-sm text-muted-foreground">{t("no_data")}</div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-3 py-3">
                  <div className="min-w-[100px] font-mono-jb text-sm" title={r.date}>{formatDate(r.date)}</div>
                  <Badge variant="outline" className="capitalize">{t(r.category)}</Badge>
                  <div className="font-mono-jb text-sm">{r.volume_m3} m³</div>
                  <div className="order-last w-full text-sm text-muted-foreground sm:order-none sm:w-auto sm:flex-1 sm:truncate">{r.user_name}: {r.description}</div>
                  <StatusBadge status={r.status} t={t} />
                  {user.role === "municipality" && r.status === "pending" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => approve(r)} data-testid={`lc-approve-${r.id}`}>
                        {t("approve")}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setStatus(r.id, "rejected")} data-testid={`lc-reject-${r.id}`}>
                        {t("reject")}
                      </Button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <OverbookingDialog check={overCheck} onCancel={() => setOverCheck(null)} onConfirm={confirmOverbooking} t={t} />
    </div>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    pending: "outline",
    approved: "default",
    rejected: "destructive",
    completed: "secondary",
  };
  return <Badge variant={map[status] || "outline"}>{t(status)}</Badge>;
}


function DayAvailabilityHint({ avail, volume, t }) {
  if (!avail) return null;
  const free = Math.round(avail.available_m3 * 10) / 10;
  const tooMuch = volume > 0 && volume > avail.available_m3;
  const tone = tooMuch || free <= 0
    ? "border-destructive/40 bg-destructive/5 text-destructive"
    : "border-primary/30 bg-primary/5 text-foreground";
  return (
    <div className={`rounded-md border px-3 py-2 text-sm ${tone}`} data-testid="lc-day-availability">
      <span className="font-medium">
        {t("lc_free_on").replace("{date}", formatDate(avail.date))}:{" "}
      </span>
      <span className="font-mono-jb" data-testid="lc-day-available-value">
        {free} / {avail.quota_m3} m³
      </span>
      {tooMuch && (
        <div className="mt-1 text-xs" data-testid="lc-day-over-hint">
          {t("lc_over_hint").replace("{over}", String(Math.round((volume - avail.available_m3) * 10) / 10))}
        </div>
      )}
    </div>
  );
}
